import {
  isEtaFieldComplete,
  isEtaFieldEmpty,
  validateEtaSlot,
  type EtaFieldValue
} from './eta-bridge'
import type { PortcallStep } from '../components/providers/create-portcall-provider/step-flow/create-portcall-steps'

/**
 * The pure declarative core of the create-portcall step flow: a state
 * machine over the ordered {@link PortcallStep} list. Every function takes
 * plain values (the step list, the current key, the pilot-station flag, the
 * per-step field value, the submit gate) — no `ref`, no IO. The composable
 * (`usePortcallWizard`) resolves the reactive state and threads it in.
 */

/**
 * The value backing one step's form field — a string for list steps, an
 * {@link EtaFieldValue} for arrival steps.
 */
export type StepFieldValue = string | EtaFieldValue | undefined

/**
 * Whether a step is shown. Only the pilot-station arrival is conditional —
 * it appears for ports that capture a separate pilot-station ETA.
 */
export function isStepVisible(
  step: PortcallStep,
  pilotStationActive: boolean
): boolean {
  return step.key === 'etaAtPilotStation' ? pilotStationActive : true
}

/** The steps shown for the current pilot-station flag, in order. */
export function visibleSteps(
  steps: readonly PortcallStep[],
  pilotStationActive: boolean
): PortcallStep[] {
  return steps.filter((s) => isStepVisible(s, pilotStationActive))
}

/** The step with the given key, or `undefined`. */
export function stepByKey(
  steps: readonly PortcallStep[],
  key: string
): PortcallStep | undefined {
  return steps.find((s) => s.key === key)
}

/** The index of the keyed step, or `-1`. */
export function stepIndex(steps: readonly PortcallStep[], key: string): number {
  return steps.findIndex((s) => s.key === key)
}

/** The step after the keyed one in the visible list, or `undefined`. */
export function nextStep(
  visible: readonly PortcallStep[],
  key: string
): PortcallStep | undefined {
  return visible[stepIndex(visible, key) + 1]
}

/** The step before the keyed one in the visible list, or `undefined`. */
export function prevStep(
  visible: readonly PortcallStep[],
  key: string
): PortcallStep | undefined {
  return visible[stepIndex(visible, key) - 1]
}

/** Whether the keyed step is the last one in the visible list. */
export function isLastStep(
  visible: readonly PortcallStep[],
  key: string
): boolean {
  return stepIndex(visible, key) === visible.length - 1
}

/**
 * Whether a step can be entered. The port-of-call step needs an agency
 * first; the arrival steps need a port first. Everything else is always
 * enterable.
 */
export function canEnterStep(
  step: PortcallStep,
  ctx: { hasAgency: boolean; hasPort: boolean }
): boolean {
  if (step.key === 'portOfCallLocationCode') return ctx.hasAgency
  if (step.key === 'etaAtPilotStation' || step.key === 'etaAtPort')
    return ctx.hasPort
  return true
}

/**
 * Whether a step is "done" enough to advance. The summary step is gated on the
 * overall submit check (it drives the create action); list steps need a value;
 * the pilot-station arrival is skippable — advanceable while empty (skip) or
 * complete, but not while partially filled (a half-finished slot can't be
 * carried forward silently); the port arrival can advance when its slot is
 * acceptable — complete, or empty while the pilot-station ETA already satisfies
 * the call. A partially-filled slot is never acceptable.
 */
export function isStepComplete(
  step: PortcallStep,
  value: StepFieldValue,
  ctx: {
    canSubmit: boolean
    pilotStationActive: boolean
    sibling?: EtaFieldValue
  }
): boolean {
  if (step.kind === 'summary') return ctx.canSubmit
  if (step.kind === 'list') return Boolean(value)
  if (step.key === 'etaAtPilotStation') {
    const eta = value as EtaFieldValue | undefined
    return isEtaFieldEmpty(eta) || isEtaFieldComplete(eta)
  }
  return (
    validateEtaSlot({
      value: value as EtaFieldValue | undefined,
      sibling: ctx.sibling,
      pilotStationActive: ctx.pilotStationActive
    }) === undefined
  )
}

/**
 * Whether a step currently holds a value (drives the sidebar tick). The summary
 * step has no field of its own — its tick reflects the created state, decided
 * by the view.
 */
export function hasValueForStep(
  step: PortcallStep,
  value: StepFieldValue
): boolean {
  if (step.kind === 'summary') return false
  if (step.kind === 'list') return Boolean(value)
  return isEtaFieldComplete(value as EtaFieldValue | undefined)
}

/** The requirement-banner state for an arrival step. */
export type ArrivalRequirementState = 'met' | 'optional' | 'required'

/**
 * The requirement state shown on an arrival step's banner. The pilot-station
 * arrival is optional until set; the port arrival is required unless the
 * pilot-station arrival already satisfies the call. The banner copy is a
 * presentation concern and lives in the view.
 */
export function arrivalRequirementState(
  step: PortcallStep,
  ctx: {
    /** The current arrival step's ETA is complete. */
    currentComplete: boolean
    /** The selected port captures a separate pilot-station ETA. */
    pilotStationActive: boolean
    /** The pilot-station ETA is complete. */
    pilotComplete: boolean
  }
): ArrivalRequirementState {
  if (step.key === 'etaAtPilotStation') {
    return ctx.currentComplete ? 'met' : 'optional'
  }
  if (ctx.currentComplete) return 'met'
  return ctx.pilotStationActive && ctx.pilotComplete ? 'optional' : 'required'
}
